/* Tathagat Media: Gendoras Box case study.
   Progressive enhancement. The page is fully readable without this file. It drives the hero reveal, split-word
   headlines, scroll reveals, parallax and the Ring Builder scrollytelling. Only transforms, opacity and clip-path are
   animated, so layout never shifts. Motion is skipped for reduced motion; the Ring Builder and rail still work. */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-gb]');
  if (!roots.length) return;

  var html = document.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var motion = !reduceMotion.matches && 'IntersectionObserver' in window && html.classList.contains('gb-has-js');

  Array.prototype.forEach.call(roots, init);
  // Tells the section's safety timer that the script is running.
  html.classList.add('gb-ready');

  function init(root) {
    press(root);
    rail(root);
    builder(root);
    if (!motion) return;
    root.classList.add('gb-js');
    hero(root);
    splitWords(root);
    reveal(root);
    scrollFx(root);
  }

  function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

  /* ---------- Hero ---------- */
  function hero(root) {
    var el = root.querySelector('[data-gb-hero]');
    if (!el) return;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { el.classList.add('is-loaded'); });
    });
  }

  /* ---------- Split headlines into words ---------- */
  function splitWords(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-gb-words]'), function (el) {
      var text = el.textContent.replace(/\s+/g, ' ').trim();
      if (!text) { el.classList.add('is-split'); return; }
      var wrap = document.createElement('span');
      wrap.setAttribute('aria-hidden', 'true');
      text.split(' ').forEach(function (word, i, all) {
        var outer = document.createElement('span');
        var inner = document.createElement('span');
        outer.className = 'gb-w';
        inner.className = 'gb-w__in';
        inner.style.setProperty('--w', i);
        inner.textContent = word;
        outer.appendChild(inner);
        wrap.appendChild(outer);
        if (i < all.length - 1) wrap.appendChild(document.createTextNode(' '));
      });
      el.setAttribute('aria-label', text);
      el.textContent = '';
      el.appendChild(wrap);
      el.classList.add('is-split');
    });
  }

  /* ---------- Scroll reveal ---------- */
  function reveal(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-gb-reveal], [data-gb-words]'));
    var pending = items.slice();
    var ticking = false;

    function show(el) {
      el.classList.add('is-in');
      io.unobserve(el);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Items already scrolled past (anchor jump, reload mid-page) must not stay hidden.
        if (entry.isIntersecting || entry.boundingClientRect.top < 0) show(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    items.forEach(function (el) { io.observe(el); });

    // The observer can miss items on fast scrolls, so sweep on scroll as a safety net.
    function sweep() {
      ticking = false;
      var limit = window.innerHeight * 0.94;
      pending = pending.filter(function (el) {
        if (el.classList.contains('is-in')) return false;
        if (el.getBoundingClientRect().top < limit) { show(el); return false; }
        return true;
      });
      if (!pending.length) window.removeEventListener('scroll', schedule);
    }

    function schedule() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(sweep);
    }

    window.addEventListener('scroll', schedule, { passive: true });
  }

  /* ---------- Parallax, drifting words and scale-in ---------- */
  function scrollFx(root) {
    var layers = Array.prototype.slice.call(root.querySelectorAll('[data-gb-par], [data-gb-drift], [data-gb-scale]'));
    var visible = new Set();
    var ticking = false;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) visible.add(entry.target); else visible.delete(entry.target);
      });
      schedule();
    }, { rootMargin: '20% 0px' });
    layers.forEach(function (el) { io.observe(el); });

    function update() {
      ticking = false;
      var vh = window.innerHeight;
      var small = window.innerWidth < 750;
      visible.forEach(function (el) {
        var r = el.getBoundingClientRect();
        var p = ((r.top + r.height / 2) - vh / 2) / (vh / 2 + r.height / 2);
        el.style.setProperty('--gb-p', clamp(p, -1, 1).toFixed(3));
        if (el.hasAttribute('data-gb-par')) {
          var amp = parseFloat(el.getAttribute('data-gb-par')) * vh * (small ? 0.5 : 1);
          el.style.setProperty('--gb-amp', amp.toFixed(1) + 'px');
        }
        if (el.hasAttribute('data-gb-scale')) {
          el.style.setProperty('--gb-in', clamp((vh - r.top) / (vh * 0.7), 0, 1).toFixed(3));
        }
      });
    }

    function schedule() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    schedule();
  }

  /* ---------- Jewellery rail: progress bar and mouse drag ---------- */
  function rail(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-gb-rail]'), function (wrap) {
      var sc = wrap.querySelector('[data-gb-rail-scroll]');
      if (!sc) return;
      var down = false, moved = false, startX = 0, startLeft = 0;

      function progress() {
        var max = sc.scrollWidth - sc.clientWidth;
        wrap.style.setProperty('--gb-rail', max > 0 ? (sc.scrollLeft / max).toFixed(3) : '0');
      }
      sc.addEventListener('scroll', progress, { passive: true });
      window.addEventListener('resize', progress);
      progress();

      // Mouse drag only. Touch and trackpad use native scrolling.
      sc.addEventListener('pointerdown', function (event) {
        if (event.pointerType !== 'mouse') return;
        down = true; moved = false; startX = event.clientX; startLeft = sc.scrollLeft;
        sc.classList.add('is-drag');
      });
      window.addEventListener('pointermove', function (event) {
        if (!down) return;
        var dx = event.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        sc.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', function () { down = false; sc.classList.remove('is-drag'); });
      sc.addEventListener('click', function (event) {
        if (moved) { event.preventDefault(); moved = false; }
      }, true);
    });
  }

  /* ---------- Ring Builder: sticky stage on desktop, swipe cards on mobile ---------- */
  function builder(root) {
    var section = root.querySelector('[data-gb-builder]');
    if (!section) return;
    var steps = Array.prototype.slice.call(section.querySelectorAll('[data-gb-step]'));
    var imgs = Array.prototype.slice.call(section.querySelectorAll('[data-gb-stage-img]'));
    var pills = Array.prototype.slice.call(section.querySelectorAll('[data-gb-pill]'));
    var track = section.querySelector('[data-gb-track]');
    var dotsWrap = section.querySelector('[data-gb-dots]');
    var mobile = window.matchMedia('(max-width: 989px)');
    var active = -1;
    var dots = [];
    if (!steps.length) return;

    if (dotsWrap) {
      steps.forEach(function (step, i) {
        var b = document.createElement('button');
        b.type = 'button';
        var t = step.querySelector('.gb-step__t');
        b.setAttribute('aria-label', t ? t.textContent : 'Step');
        b.addEventListener('click', function () {
          var left = step.offsetLeft - (track.clientWidth - step.clientWidth) / 2;
          track.scrollTo({ left: left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        });
        dotsWrap.appendChild(b);
        dots.push(b);
      });
    }

    function setActive(i) {
      if (i === active) return;
      active = i;
      var level = parseInt(steps[i].getAttribute('data-level'), 10) || 0;
      steps.forEach(function (s, j) { s.classList.toggle('is-active', j === i); });
      imgs.forEach(function (img, j) {
        img.classList.toggle('is-active', j === i);
        img.classList.toggle('is-before', j < i);
      });
      pills.forEach(function (p) {
        var l = parseInt(p.getAttribute('data-level'), 10);
        p.classList.toggle('is-on', l <= level);
        if (l === level) p.setAttribute('aria-current', 'step'); else p.removeAttribute('aria-current');
      });
      dots.forEach(function (d, j) {
        if (j === i) d.setAttribute('aria-current', 'step'); else d.removeAttribute('aria-current');
      });
      section.style.setProperty('--gb-prog', steps.length > 1 ? (i / (steps.length - 1)).toFixed(3) : '1');
    }

    // Desktop: the step crossing the middle of the screen becomes active.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (mobile.matches) return;
        entries.forEach(function (entry) {
          if (entry.isIntersecting) setActive(steps.indexOf(entry.target));
        });
      }, { rootMargin: '-42% 0px -42% 0px', threshold: 0 });
      steps.forEach(function (s) { io.observe(s); });
    }

    // Mobile: the card nearest the centre of the swipe track becomes active.
    var ticking = false;
    function nearest() {
      ticking = false;
      if (!mobile.matches) return;
      var centre = track.scrollLeft + track.clientWidth / 2;
      var best = 0, bestDist = Infinity;
      steps.forEach(function (s, j) {
        var d = Math.abs(s.offsetLeft + s.clientWidth / 2 - centre);
        if (d < bestDist) { bestDist = d; best = j; }
      });
      setActive(best);
    }
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(nearest);
    }, { passive: true });
    window.addEventListener('resize', nearest);

    setActive(0);
  }

  /* ---------- Touch press feedback ---------- */
  function press(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-gb-press]'), function (el) {
      function release() { el.classList.remove('is-pressed'); }
      el.addEventListener('pointerdown', function (event) {
        if (event.pointerType !== 'mouse') el.classList.add('is-pressed');
      });
      el.addEventListener('pointerup', release);
      el.addEventListener('pointercancel', release);
      el.addEventListener('pointerleave', release);
    });
  }
})();
